import { Location } from '../types';

export const odysseyLocations: Location[] = [
  {
    id: 1,
    name: "Troy",
    modernName: "Hisarlik, Turkey",
    coordinates: { lat: 39.9575, lng: 26.2389 },
    book: "Prologue (Iliad context)",
    phase: "departure",
    description: "After 10 years of siege, the Greeks finally capture Troy using Odysseus's wooden horse stratagem. Victorious but weary, Odysseus and his 12 ships set sail for home, not knowing the gods have other plans.",
    mythStory: "The great walled city of King Priam fell through Odysseus's cunning - the famed Trojan Horse. As flames consumed the city, the Greek heroes divided their spoils and prepared for the journey home.",
    modernInfo: "Today, the archaeological site of Troy (Truva) near Çanakkale reveals nine layers of ancient cities. Visitors can see reconstructed walls, the famous replica wooden horse, and the Troy Museum.",
    thingsToDo: [
      { name: "Troy Archaeological Site", type: "attraction", description: "UNESCO World Heritage Site with 4,000 years of history" },
      { name: "Troy Museum", type: "museum", description: "Modern museum housing artifacts from excavations" },
      { name: "Wooden Horse Replica", type: "photo-op", description: "Climb inside the iconic wooden horse" }
    ],
    images: ["/images/locations/troy-ruins.jpg", "/images/locations/wooden-horse.jpg"],
    importance: "major",
    journeyDay: 1
  },
  {
    id: 2,
    name: "Ismarus (Land of the Cicones)",
    modernName: "Maroneia, Thrace, Greece",
    coordinates: { lat: 40.8833, lng: 25.5167 },
    book: "Book IX",
    phase: "wanderings",
    description: "The first stop after Troy. Odysseus's men sack the city and plunder its wealth, but they linger too long. Reinforcements arrive and attack, killing 72 of Odysseus's men - six from each ship.",
    mythStory: "Drunk on victory and wine, the men ignored Odysseus's warnings to leave quickly. The Cicones' allies descended at dawn, and the Greeks barely escaped with their lives - a harsh lesson about hubris.",
    modernInfo: "The ancient region of Thrace is now northeastern Greece. Maroneia was famous in antiquity for its wine. The area features Byzantine churches, ancient walls, and stunning Aegean coastline.",
    thingsToDo: [
      { name: "Maroneia Archaeological Site", type: "attraction", description: "Ancient theater and sanctuary ruins" },
      { name: "Local Wineries", type: "experience", description: "Taste wines from one of Greece's oldest wine regions" },
      { name: "Maronia Beach", type: "beach", description: "Crystal-clear waters near the ancient port" }
    ],
    images: ["/images/locations/thrace-coast.jpg"],
    importance: "minor",
    journeyDay: 10
  },
  {
    id: 3,
    name: "Land of the Lotus-Eaters",
    modernName: "Djerba, Tunisia",
    coordinates: { lat: 33.8076, lng: 10.8451 },
    book: "Book IX",
    phase: "wanderings",
    description: "Blown off course by storms for nine days, Odysseus lands on an island where the inhabitants eat lotus flowers that cause forgetfulness. Three scouts eat the lotus and lose all desire to return home.",
    mythStory: "The lotus fruit brought blissful forgetfulness - those who ate it wanted only to stay and dream forever. Odysseus had to drag his weeping men back to the ships and tie them to the benches.",
    modernInfo: "Djerba is Tunisia's largest island, known for its white-sand beaches, traditional architecture, and the ancient El Ghriba Synagogue. It's a popular resort destination with a unique cultural heritage.",
    thingsToDo: [
      { name: "El Ghriba Synagogue", type: "religious", description: "One of the oldest synagogues in the world (2,500+ years)" },
      { name: "Houmt Souk", type: "market", description: "Traditional medina with crafts and spices" },
      { name: "Djerba Beaches", type: "beach", description: "Pristine Mediterranean beaches and resorts" },
      { name: "Djerbahood Street Art", type: "art", description: "Open-air museum of street art by 150+ artists" }
    ],
    images: ["/images/locations/djerba-beach.jpg", "/images/locations/el-ghriba.jpg"],
    importance: "major",
    journeyDay: 19
  },
  {
    id: 4,
    name: "Land of the Cyclopes",
    modernName: "Eastern Sicily, Italy (near Mount Etna)",
    coordinates: { lat: 37.7510, lng: 14.9934 },
    book: "Book IX",
    phase: "wanderings",
    description: "Odysseus and twelve men enter the cave of Polyphemus, a one-eyed giant and son of Poseidon. The Cyclops traps them, eating six men before Odysseus blinds him with a sharpened stake and escapes.",
    mythStory: "Using wit over strength, Odysseus got the giant drunk, told him his name was 'Nobody,' then blinded him. When Polyphemus cried for help, the other Cyclopes ignored him: 'If Nobody is hurting you, it must be the gods.' Odysseus escaped clinging to the belly of a ram.",
    modernInfo: "The rocky coastline near Aci Trezza features the Faraglioni (sea stacks) - said to be the boulders Polyphemus hurled at Odysseus's escaping ships. Mount Etna looms dramatically over the landscape.",
    thingsToDo: [
      { name: "Aci Trezza Faraglioni", type: "landmark", description: "Legendary rocks thrown by Polyphemus" },
      { name: "Mount Etna Tours", type: "adventure", description: "Hike or cable car up Europe's tallest active volcano" },
      { name: "Taormina", type: "town", description: "Stunning hilltop town with ancient Greek theater" },
      { name: "Catania Fish Market", type: "food", description: "Experience authentic Sicilian seafood culture" }
    ],
    images: ["/images/locations/faraglioni.jpg", "/images/locations/mount-etna.jpg"],
    importance: "major",
    journeyDay: 30,
    nolansFilmLocation: true
  },
  {
    id: 5,
    name: "Island of Aeolus (Keeper of the Winds)",
    modernName: "Lipari / Stromboli, Aeolian Islands, Italy",
    coordinates: { lat: 38.4844, lng: 14.9500 },
    book: "Book X",
    phase: "wanderings",
    description: "Aeolus, king of the winds, hosts Odysseus for a month. He gives him a leather bag containing all contrary winds, leaving only the West Wind to blow them home. Within sight of Ithaca, curious crewmen open the bag, and storms blow them all the way back.",
    mythStory: "For nine days they sailed with the favorable wind. Odysseus could see the fires of home. But when he slept, his men - suspecting treasure - opened the bag. The unleashed winds created a hurricane that swept them back to Aeolus, who refused to help again, believing them cursed by the gods.",
    modernInfo: "The Aeolian Islands are a UNESCO World Heritage volcanic archipelago. Stromboli features an active volcano that erupts regularly (visible at night), while Lipari has excellent archaeological museums.",
    thingsToDo: [
      { name: "Stromboli Night Hike", type: "adventure", description: "Witness volcanic eruptions after sunset" },
      { name: "Lipari Archaeological Museum", type: "museum", description: "Exceptional Greek and Roman artifacts" },
      { name: "Island Hopping", type: "experience", description: "Boat tours between the seven islands" },
      { name: "Thermal Baths of Vulcano", type: "wellness", description: "Volcanic mud baths and hot springs" }
    ],
    images: ["/images/locations/stromboli-night.jpg", "/images/locations/lipari-coast.jpg"],
    importance: "major",
    journeyDay: 40,
    nolansFilmLocation: true
  },
  {
    id: 6,
    name: "Laestrygonia (Land of the Giant Cannibals)",
    modernName: "Bonifacio, Corsica, France",
    coordinates: { lat: 41.3872, lng: 9.1593 },
    book: "Book X",
    phase: "wanderings",
    description: "The Greeks enter a fjord-like harbor where giant cannibals hurl boulders from the cliffs. Eleven of Odysseus's twelve ships are destroyed; only his survives because he anchored outside the harbor.",
    mythStory: "The Laestrygonians were giants who feasted on human flesh. They speared the Greeks like fish and devoured them. It was a massacre - from 12 ships and 600 men, only Odysseus's single ship with 45 crew escaped.",
    modernInfo: "Bonifacio sits dramatically on white limestone cliffs overlooking a narrow harbor - matching Homer's description perfectly. The medieval citadel and stunning coastline make it one of Corsica's most spectacular sites.",
    thingsToDo: [
      { name: "Bonifacio Citadel", type: "historic", description: "Medieval fortress on dramatic cliffs" },
      { name: "Sea Cave Boat Tours", type: "adventure", description: "Explore grottoes and the famous 'King of Aragon's Stairway'" },
      { name: "Lavezzi Islands", type: "nature", description: "Pristine marine reserve for snorkeling" }
    ],
    images: ["/images/locations/bonifacio-cliffs.jpg"],
    importance: "minor",
    journeyDay: 50
  },
  {
    id: 7,
    name: "Aeaea (Island of Circe)",
    modernName: "Monte Circeo, Lazio, Italy",
    coordinates: { lat: 41.2282, lng: 13.0654 },
    book: "Book X",
    phase: "wanderings",
    description: "The witch-goddess Circe transforms half of Odysseus's men into pigs. With Hermes's help, Odysseus resists her magic and becomes her lover. They stay for one year before Circe reveals he must visit the Underworld.",
    mythStory: "Circe lived in a stone palace surrounded by docile wolves and lions - all former men. She drugged the Greeks' wine and turned them to swine. But Odysseus, protected by the herb moly, drew his sword and forced her to restore his men. They became lovers, and she helped him plan the next stage of his journey.",
    modernInfo: "Monte Circeo is a promontory on Italy's Tyrrhenian coast, once an island. The Circeo National Park features pristine beaches, coastal caves, and Mediterranean forest. Ancient traditions tie this dramatically beautiful spot to the goddess.",
    thingsToDo: [
      { name: "Circeo National Park", type: "nature", description: "Hiking trails through ancient forest to the summit" },
      { name: "Sabaudia Beach", type: "beach", description: "One of Italy's most beautiful beaches" },
      { name: "Grotta della Maga Circe", type: "cave", description: "Cave associated with the Circe legend" },
      { name: "San Felice Circeo Town", type: "town", description: "Charming historic village with panoramic views" }
    ],
    images: ["/images/locations/monte-circeo.jpg", "/images/locations/sabaudia-beach.jpg"],
    importance: "major",
    journeyDay: 60
  },
  {
    id: 8,
    name: "Entrance to the Underworld (Nekyia)",
    modernName: "Necromanteion / Acheron River, Epirus, Greece",
    coordinates: { lat: 39.2347, lng: 20.5347 },
    book: "Book XI",
    phase: "wanderings",
    description: "Following Circe's instructions, Odysseus sails to the edge of the world to consult the dead. He speaks with the prophet Tiresias, his mother Anticlea, and fallen comrades from Troy including Achilles and Agamemnon.",
    mythStory: "Odysseus dug a pit and filled it with blood sacrifice. The spirits of the dead gathered, desperate to drink. Tiresias prophesied that Odysseus would reach home, but warned of the dangers ahead and the suitors plaguing his household.",
    modernInfo: "The Necromanteion was an actual ancient Greek temple of the dead beside the Acheron River. Visitors can explore the underground chambers where ancient Greeks came to consult departed souls.",
    thingsToDo: [
      { name: "Necromanteion Archaeological Site", type: "historic", description: "Ancient 'Oracle of the Dead' with underground passages" },
      { name: "Acheron River Rafting", type: "adventure", description: "Raft the mythological 'River of Woe'" },
      { name: "Acheron Springs", type: "nature", description: "Crystal-clear springs with stunning blue water" },
      { name: "Parga Town", type: "town", description: "Picturesque coastal town nearby" }
    ],
    images: ["/images/locations/necromanteion.jpg", "/images/locations/acheron-river.jpg"],
    importance: "major",
    journeyDay: 425
  },
  {
    id: 9,
    name: "Island of the Sirens",
    modernName: "Li Galli Islands, Amalfi Coast, Italy",
    coordinates: { lat: 40.5797, lng: 14.4247 },
    book: "Book XII",
    phase: "wanderings",
    description: "The Sirens sing so beautifully that sailors crash their ships trying to reach them. Odysseus has his men plug their ears with wax while he is bound to the mast - the only mortal to hear their song and survive.",
    mythStory: "Circe warned that the Sirens' song promised knowledge of all things. Odysseus, ever curious, wanted to hear them. As their voices filled his ears, he strained against his bonds, begging to be released. His men only rowed harder until the song faded.",
    modernInfo: "The Li Galli islands, also called 'Le Sirenuse,' sit just off the Amalfi Coast near Positano. Once owned by Rudolf Nureyev, they're visible from many points along this spectacular coastline.",
    thingsToDo: [
      { name: "Li Galli Boat Tour", type: "boat", description: "Sail past the legendary Siren islands" },
      { name: "Positano", type: "town", description: "Iconic pastel-colored cliffside village" },
      { name: "Path of the Gods Hike", type: "hiking", description: "Dramatic coastal trail with Siren island views" },
      { name: "Amalfi Coast Drive", type: "scenic", description: "One of the world's most beautiful drives" }
    ],
    images: ["/images/locations/li-galli.jpg", "/images/locations/positano.jpg"],
    importance: "major",
    journeyDay: 426
  },
  {
    id: 10,
    name: "Scylla and Charybdis",
    modernName: "Strait of Messina, Italy",
    coordinates: { lat: 38.2356, lng: 15.6269 },
    book: "Book XII",
    phase: "wanderings",
    description: "Odysseus must navigate between two terrors: the six-headed monster Scylla who devours sailors, and Charybdis, a whirlpool that swallows ships whole. Following Circe's advice, he sails closer to Scylla, losing six men but saving the ship.",
    mythStory: "There was no safe passage. Charybdis would destroy the entire ship; Scylla would take only six. Odysseus watched helplessly as his strongest men were snatched from the deck - 'the most pitiful thing I saw in all my travels on the sea.'",
    modernInfo: "The Strait of Messina between Sicily and Calabria is known for powerful currents and whirlpools. The town of Scilla on the Calabrian coast bears the monster's name. Ferries cross the strait regularly today.",
    thingsToDo: [
      { name: "Scilla Village", type: "town", description: "Picturesque fishing village named for the monster" },
      { name: "Ruffo Castle", type: "castle", description: "Medieval fortress overlooking the strait" },
      { name: "Chianalea Neighborhood", type: "historic", description: "'Little Venice' - houses built over the water" },
      { name: "Strait Crossing", type: "ferry", description: "Cross where Odysseus sailed" }
    ],
    images: ["/images/locations/scilla-village.jpg", "/images/locations/messina-strait.jpg"],
    importance: "major",
    journeyDay: 427
  },
  {
    id: 11,
    name: "Thrinacia (Island of Helios's Cattle)",
    modernName: "Sicily, Italy (general)",
    coordinates: { lat: 37.5994, lng: 14.0154 },
    book: "Book XII",
    phase: "wanderings",
    description: "Trapped on the island by storms, Odysseus's starving men kill the sacred cattle of the Sun God despite his warnings. When they sail, Zeus destroys their ship with a thunderbolt. Only Odysseus survives.",
    mythStory: "For a month, they ate fish and birds. Finally, while Odysseus prayed, his men slaughtered the immortal cattle. The hides crawled and the meat lowed on the spit. Zeus's vengeance was swift - all perished except Odysseus, who clung to wreckage for nine days.",
    modernInfo: "Sicily, the triangular island, was known to Greeks as Trinacria. It's Italy's largest island, home to Greek temples, baroque cities, and Europe's most active volcano.",
    thingsToDo: [
      { name: "Valley of the Temples, Agrigento", type: "ruins", description: "Best-preserved Greek temples outside Greece" },
      { name: "Palermo Historic Markets", type: "market", description: "Vibrant street food and culture" },
      { name: "Syracuse Ortigia", type: "historic", description: "Ancient Greek city island" },
      { name: "Favignana Island", type: "island", description: "Nolan's filming location - 'Goat Island'" }
    ],
    images: ["/images/locations/valley-temples.jpg", "/images/locations/favignana.jpg"],
    importance: "major",
    journeyDay: 428,
    nolansFilmLocation: true
  },
  {
    id: 12,
    name: "Ogygia (Island of Calypso)",
    modernName: "Gozo, Malta",
    coordinates: { lat: 36.0440, lng: 14.2517 },
    book: "Books I, V, VII",
    phase: "wanderings",
    description: "The nymph Calypso rescues Odysseus from the sea and keeps him on her island for seven years, offering him immortality if he stays. He refuses, longing for home and Penelope. Finally, the gods order his release.",
    mythStory: "Every day, Odysseus sat on the shore weeping, gazing toward Ithaca. Calypso loved him and offered eternal youth. But Odysseus chose mortality and home over paradise. With her help, he built a raft and set sail after seven long years.",
    modernInfo: "Gozo, Malta's sister island, features 'Calypso's Cave' overlooking the red sands of Ramla Bay. The island has Neolithic temples older than the pyramids, dramatic coastline, and traditional village life.",
    thingsToDo: [
      { name: "Calypso's Cave", type: "landmark", description: "Legendary cave overlooking Ramla Bay" },
      { name: "Ggantija Temples", type: "ruins", description: "5,500-year-old megalithic temples (UNESCO)" },
      { name: "Dwejra/Azure Window Area", type: "nature", description: "Dramatic coastal formations" },
      { name: "Cittadella", type: "historic", description: "Ancient fortified city" }
    ],
    images: ["/images/locations/ramla-bay.jpg", "/images/locations/calypso-cave.jpg"],
    importance: "major",
    journeyDay: 2920,
    nolansFilmLocation: true
  },
  {
    id: 13,
    name: "Scheria (Land of the Phaeacians)",
    modernName: "Corfu (Kerkyra), Greece",
    coordinates: { lat: 39.6243, lng: 19.9217 },
    book: "Books VI-VIII, XIII",
    phase: "return",
    description: "After his raft is destroyed by Poseidon, Odysseus washes ashore and is found by Princess Nausicaa. King Alcinous hosts him royally, and here Odysseus tells the full story of his wanderings. The Phaeacians finally transport him home.",
    mythStory: "Naked and covered in brine, Odysseus emerged from the bushes before the princess and her maidens. Athena made him appear godlike. At the palace, he wept when the bard sang of Troy. Over three nights, he told his tale. The Phaeacians, the greatest sailors, carried him sleeping to Ithaca.",
    modernInfo: "Corfu is one of Greece's most beautiful and historic islands, with Venetian, French, and British colonial heritage. Its Old Town is a UNESCO World Heritage Site. Mon Repos palace is where Prince Philip was born.",
    thingsToDo: [
      { name: "Corfu Old Town", type: "historic", description: "UNESCO World Heritage Venetian architecture" },
      { name: "Achilleion Palace", type: "palace", description: "Empress Sisi's Greek mythology-themed palace" },
      { name: "Paleokastritsa", type: "beach", description: "Stunning bays said to be where Odysseus landed" },
      { name: "Old Fortress", type: "fortress", description: "Venetian fortress with panoramic views" }
    ],
    images: ["/images/locations/corfu-old-town.jpg", "/images/locations/paleokastritsa.jpg"],
    importance: "major",
    journeyDay: 2937,
    nolansFilmLocation: true
  },
  {
    id: 14,
    name: "Ithaca (Home)",
    modernName: "Ithaca (Ithaki), Greece",
    coordinates: { lat: 38.3654, lng: 20.7190 },
    book: "Books XIII-XXIV",
    phase: "return",
    description: "After 20 years, Odysseus finally returns to his island kingdom. Disguised as a beggar by Athena, he must reclaim his home from the suitors pursuing Penelope and reunite with his faithful wife and grown son Telemachus.",
    mythStory: "Only his old dog Argos recognized him - then died content. Odysseus revealed himself to Telemachus. Together they planned vengeance. In the great hall, Odysseus strung his own bow - which no suitor could bend - and slaughtered them all. Penelope tested him with the secret of their bed. At last, they were reunited.",
    modernInfo: "The small island of Ithaca in the Ionian Sea remains relatively unspoiled. Visitors can see the 'Cave of the Nymphs' where Odysseus hid treasure, hike to possible palace sites, and experience the timeless Greek island life.",
    thingsToDo: [
      { name: "Cave of the Nymphs", type: "cave", description: "Where Odysseus hid Phaeacian treasures" },
      { name: "School of Homer", type: "ruins", description: "Archaeological site possibly linked to Odysseus's palace" },
      { name: "Vathy Harbor", type: "town", description: "Charming capital in a natural harbor" },
      { name: "Mount Neriton Hike", type: "hiking", description: "Climb the mountain Homer described" },
      { name: "Kioni Village", type: "village", description: "Picturesque traditional harbor village" }
    ],
    images: ["/images/locations/ithaca-harbor.jpg", "/images/locations/cave-nymphs.jpg"],
    importance: "major",
    journeyDay: 3650,
    isDestination: true
  },
  {
    id: 15,
    name: "Pylos",
    modernName: "Pylos, Messenia, Greece",
    coordinates: { lat: 36.9125, lng: 21.6944 },
    book: "Books III, XV",
    phase: "telemachus",
    description: "Telemachus visits wise King Nestor seeking news of his father. Nestor's palace has been excavated nearby.",
    mythStory: "Young Telemachus, guided by Athena (disguised as Mentor), sailed to Pylos to learn of his father's fate. Nestor, the eldest of the Greek heroes, received him with honor but could only say Odysseus had left Troy alive.",
    modernInfo: "Pylos is a charming seaside town with a Venetian fortress. Nearby, the Palace of Nestor archaeological site offers remarkably preserved Bronze Age remains including a throne room and administrative archives.",
    thingsToDo: [
      { name: "Palace of Nestor", type: "ruins", description: "Mycenaean palace ruins with Linear B tablets" },
      { name: "Niokastro Fortress", type: "fortress", description: "Well-preserved Venetian castle" },
      { name: "Navarino Bay", type: "historic", description: "Site of historic naval battle of 1827" }
    ],
    images: ["/images/locations/pylos.jpg"],
    importance: "minor",
    journeyDay: 3655
  },
  {
    id: 16,
    name: "Sparta",
    modernName: "Sparta, Laconia, Greece",
    coordinates: { lat: 37.0739, lng: 22.4297 },
    book: "Books IV, XV",
    phase: "telemachus",
    description: "Telemachus visits Menelaus and Helen, who tell him Odysseus is alive on Calypso's island.",
    mythStory: "In the palace of Menelaus, Telemachus marveled at the splendor - gold and amber gleaming everywhere. Helen recognized him by his resemblance to Odysseus. Menelaus revealed that the Old Man of the Sea had told him Odysseus lived, held captive by a goddess on a distant island.",
    modernInfo: "Modern Sparta is a modest town, but nearby archaeological sites include ancient Sparta ruins and the spectacular Byzantine ruins of Mystras on a mountainside.",
    thingsToDo: [
      { name: "Mystras", type: "ruins", description: "UNESCO Byzantine fortress-city with stunning frescoes" },
      { name: "Museum of the Olive and Greek Olive Oil", type: "museum", description: "Explore ancient olive cultivation" },
      { name: "Ancient Sparta Ruins", type: "ruins", description: "Remains of the legendary warrior city" }
    ],
    images: ["/images/locations/mystras.jpg"],
    importance: "minor",
    journeyDay: 3660
  },
  {
    id: 17,
    name: "Mount Olympus",
    modernName: "Mount Olympus, Greece",
    coordinates: { lat: 40.0859, lng: 22.3584 },
    book: "Throughout",
    phase: "gods",
    description: "Home of the gods who debate Odysseus's fate. Athena champions him; Poseidon opposes.",
    mythStory: "High on Olympus, the immortal gods gathered in council. Athena pleaded for Odysseus's release, while Poseidon raged against the man who blinded his son. Zeus, father of gods and men, decreed that after twenty years of wandering, the hero would finally reach home.",
    modernInfo: "Greece's highest mountain (2,918m) straddles the border of Thessaly and Macedonia. A UNESCO Biosphere Reserve, it offers world-class hiking through forests and alpine zones to the peaks where the gods once dwelled.",
    thingsToDo: [
      { name: "Olympus Summit Hike", type: "hiking", description: "Multi-day trek to Mytikas peak (9,573 ft)" },
      { name: "Enipeas Gorge", type: "nature", description: "Stunning gorge hike with waterfalls" },
      { name: "Dion Archaeological Park", type: "ruins", description: "Ancient Macedonian sanctuary at mountain's base" }
    ],
    images: ["/images/locations/mount-olympus.jpg"],
    importance: "minor",
    journeyDay: 0
  }
];
